import React from 'react';
import type { TreeNode } from '../BreadcrumbTreeView/types';

interface UseFileListScrollProps {
  data: TreeNode | TreeNode[];
  selectedNodeId?: string;
  searchValue?: string;
  isLoadingData: boolean;
}

export const useFileListScroll = ({
  data,
  selectedNodeId,
  searchValue,
  isLoadingData,
}: UseFileListScrollProps) => {
  const virtuosoRef = React.useRef<any>(null);
  const lastScrollTopRef = React.useRef<number>(0);
  const shouldRestoreScrollRef = React.useRef<boolean>(false);
  const dataChangeCountRef = React.useRef<number>(0);
  const isRestoringScrollRef = React.useRef<boolean>(false);
  const previousDataLengthRef = React.useRef<number>(0);
  const hasScrolledToSelectedRef = React.useRef<boolean>(false);
  const isInitialLoadRef = React.useRef<boolean>(true);

  const items = React.useMemo(() => (!Array.isArray(data) ? (data ? [data] : []) : data), [data]);

  const filteredItems = React.useMemo(() => {
    if (!searchValue) return items;
    return items.filter((item) => {
      const searchLower = searchValue.toLowerCase();
      return (item.displayName || item.name).toLowerCase().includes(searchLower);
    });
  }, [items, searchValue]);

  // Store scroll position using Virtuoso's scroll event
  const handleVirtuosoScroll = React.useCallback((e: any) => {
    const scrollTop = e.target?.scrollTop || 0;

    // Don't update the stored position if we're in the middle of restoring scroll
    if (!isRestoringScrollRef.current) {
      lastScrollTopRef.current = scrollTop;
      shouldRestoreScrollRef.current = true;
    }
  }, []);

  // Detect if we're navigating into a folder (should reset to top) vs changing data at same level
  const shouldResetToTop = React.useMemo(() => {
    const currentLength = filteredItems.length;
    const previousLength = previousDataLengthRef.current;

    // If this is the first load, don't reset
    if (previousLength === 0) {
      return false;
    }

    // If data length changed significantly (likely navigating into/out of folder), reset to top
    const lengthDifference = Math.abs(currentLength - previousLength);
    const shouldReset = lengthDifference > 10; // Threshold for considering it a navigation

    return shouldReset;
  }, [filteredItems.length]);

  // Scroll to selected item only on initial load when component mounts with a selectedNodeId
  React.useEffect(() => {
    if (
      selectedNodeId &&
      filteredItems.length > 0 &&
      !hasScrolledToSelectedRef.current &&
      !isLoadingData &&
      isInitialLoadRef.current
    ) {
      const selectedIndex = filteredItems.findIndex((item) => item.id === selectedNodeId);

      if (selectedIndex !== -1 && virtuosoRef.current) {
        // Reset the flag so we don't scroll again on subsequent renders
        hasScrolledToSelectedRef.current = true;

        // Scroll to the selected item with a small delay to ensure the list is rendered
        setTimeout(() => {
          if (virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({
              index: selectedIndex,
              align: 'center',
              offset: 0,
            });
          }
        }, 100);
      }
    }
  }, [selectedNodeId, filteredItems, isLoadingData]);

  // Mark initial load as complete only after we've attempted to scroll or have enough data
  React.useEffect(() => {
    // Only mark initial load as complete if:
    // 1. We have data AND
    // 2. Either we've already scrolled to selected item OR we have enough data to be confident the selected item won't be found
    if (
      filteredItems.length > 0 &&
      isInitialLoadRef.current &&
      (hasScrolledToSelectedRef.current ||
        (filteredItems.length > 10 && !selectedNodeId) || // No selected item and we have substantial data
        (filteredItems.length > 10 &&
          selectedNodeId &&
          !filteredItems.some((item) => item.id === selectedNodeId))) // Selected item not found in substantial data
    ) {
      isInitialLoadRef.current = false;
    }
  }, [filteredItems.length, selectedNodeId]);

  // Restore scroll position after data update with immediate restoration
  React.useEffect(() => {
    // Update the previous length for next comparison
    previousDataLengthRef.current = filteredItems.length;

    if (shouldResetToTop) {
      // Navigating into/out of folder - reset to top
      hasScrolledToSelectedRef.current = false; // Reset flag when navigating

      if (virtuosoRef.current) {
        virtuosoRef.current.scrollTo({ top: 0 });
      }
      shouldRestoreScrollRef.current = false;
    } else if (shouldRestoreScrollRef.current && lastScrollTopRef.current > 10) {
      // Same level data change - preserve scroll position
      isRestoringScrollRef.current = true;

      // Immediate restoration
      if (virtuosoRef.current) {
        virtuosoRef.current.scrollTo({ top: lastScrollTopRef.current });
      }

      // Multiple restoration attempts to ensure it sticks
      const attempts = [10, 50, 100, 200];
      attempts.forEach((delay, index) => {
        setTimeout(() => {
          if (virtuosoRef.current && isRestoringScrollRef.current) {
            virtuosoRef.current.scrollTo({ top: lastScrollTopRef.current });
          }
        }, delay);
      });

      // Clear the restoration flag after all attempts
      setTimeout(() => {
        isRestoringScrollRef.current = false;
      }, 250);
    }
  }, [filteredItems, shouldResetToTop]);

  // Log when data prop changes
  React.useEffect(() => {
    dataChangeCountRef.current += 1;
  }, [data]);

  return {
    virtuosoRef,
    filteredItems,
    handleVirtuosoScroll,
  };
};
